/**
 * What each account role may see in the Sync Monitor dashboard.
 *
 * This is presentation only — hiding a nav item never grants or denies
 * anything. The server enforces the same split in
 * `util/staffRouteAccess.js`, and the two must be changed together: a page
 * shown here whose API calls are not on the server allowlist renders errors,
 * and a page hidden here is still reachable by URL until the server refuses.
 */

export const ROLE_ADMIN = 'admin';
export const ROLE_STAFF = 'staff';

/** Route paths (relative to the router basename) each role may open. */
const PAGES_BY_ROLE = {
  [ROLE_ADMIN]: ['', 'data', 'analytics', 'schedule', 'logs', 'webhooks', 'settings'],
  [ROLE_STAFF]: ['data', 'analytics'],
};

/** Data page sub-nav tab keys each role may open. */
const DATA_TABS_BY_ROLE = {
  [ROLE_ADMIN]: [
    'batches', 'mentors', 'hierarchy', 'courses', 'chapters',
    'exams', 'calendar', 'month-planner', 'exam-attempts',
  ],
  [ROLE_STAFF]: ['mentors', 'hierarchy', 'calendar', 'month-planner'],
};

/** Where each role lands when it opens the dashboard root. */
const LANDING_PAGE_BY_ROLE = {
  [ROLE_ADMIN]: '',
  [ROLE_STAFF]: 'data',
};

/**
 * An unrecognised role gets nothing. The dashboard is admin tooling, so an
 * unknown role means the token is older or newer than this build — showing it
 * an empty shell is the safe reading.
 */
const forRole = (table, role) => table[role] || [];

/**
 * @param {string} role
 * @param {string} pagePath Route path without a leading slash; '' is the index.
 * @returns {boolean}
 */
export const canAccessPage = (role, pagePath) =>
  forRole(PAGES_BY_ROLE, role).includes(pagePath);

/**
 * @param {string} role
 * @param {string} tabKey A `DataPage` sub-nav key.
 * @returns {boolean}
 */
export const canAccessDataTab = (role, tabKey) =>
  forRole(DATA_TABS_BY_ROLE, role).includes(tabKey);

/**
 * @param {string} role
 * @returns {string[]} the sub-nav tab keys this role may open, in no
 *   particular order — callers filter their own ordered tab list with
 *   `canAccessDataTab` so the on-screen order stays theirs.
 */
export const allowedDataTabs = (role) => forRole(DATA_TABS_BY_ROLE, role);

/**
 * @param {string} role
 * @returns {string} the path this role should be sent to instead of a page it
 *   may not open. Falls back to the login-adjacent root for unknown roles.
 */
export const landingPageForRole = (role) => LANDING_PAGE_BY_ROLE[role] ?? '';
